import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useGetUserSubscriptionQuery, useLazyGetCandidatesQuery, usePrefetch } from 'services';
import { ProgAIResults, ProgAISearchResult, SearchParams } from 'shared/generated-models';
import { SEARCH_LOADING_SIZE } from 'features/search/models';

import { useFilters } from './use-filters';

interface UseGetCandidatesInfiniteResult {
	results: ProgAIResults[];
	resultsCount?: number;
	searchResult?: ProgAISearchResult;
	totalLoaded: number;
	isFetchingInitial: boolean;
	isFetchingMore: boolean;
	isError: boolean;
	hasMore: boolean;
	loadMore: () => Promise<boolean>;
}

const getBaseFilters = (filters: SearchParams): SearchParams => {
	const { page: _page, size: _size, id: _id, ...baseFilters } = filters;

	return baseFilters;
};

export const useGetCandidatesInfinite = (): UseGetCandidatesInfiniteResult => {
	const filters = useFilters();
	const filtersKey = useMemo(() => JSON.stringify(getBaseFilters(filters)), [filters]);
	const baseFilters = useMemo(() => getBaseFilters(filters), [filtersKey]);

	const [results, setResults] = useState<ProgAIResults[]>([]);
	const [resultsCount, setResultsCount] = useState<number>();
	const [searchResult, setSearchResult] = useState<ProgAISearchResult>();
	const [currentPage, setCurrentPage] = useState(-1);
	const [lastBatchSize, setLastBatchSize] = useState(0);
	const [isFetchingInitial, setIsFetchingInitial] = useState(true);
	const [isFetchingMore, setIsFetchingMore] = useState(false);
	const [isError, setIsError] = useState(false);

	const requestVersionRef = useRef(0);

	const [getCandidates] = useLazyGetCandidatesQuery();
	const prefetchPage = usePrefetch('getCandidates');
	const { refetch: refetchSubscription } = useGetUserSubscriptionQuery();

	const fetchPage = useCallback(
		async (page: number, mode: 'replace' | 'append') => {
			const requestVersion = ++requestVersionRef.current;

			setIsError(false);

			if (mode === 'replace') {
				setIsFetchingInitial(true);
			} else {
				setIsFetchingMore(true);
			}

			try {
				const response = await getCandidates({
					...baseFilters,
					id: undefined,
					ai_search: undefined,
					page,
					size: SEARCH_LOADING_SIZE
				}).unwrap();

				if (requestVersion !== requestVersionRef.current) return false;

				setResults(previousResults =>
					mode === 'replace' ? response.results : [...previousResults, ...response.results]
				);
				setResultsCount(response.results_count);
				if (mode === 'replace') {
					setSearchResult(response);
				}
				setCurrentPage(page);
				setLastBatchSize(response.results.length);
				refetchSubscription();

				return response.results.length > 0;
			} catch (error) {
				if (requestVersion === requestVersionRef.current) {
					setIsError(true);
				}

				return false;
			} finally {
				if (requestVersion === requestVersionRef.current) {
					setIsFetchingInitial(false);
					setIsFetchingMore(false);
				}
			}
		},
		[baseFilters, getCandidates, refetchSubscription]
	);

	useEffect(() => {
		requestVersionRef.current += 1;
		setResults([]);
		setResultsCount(undefined);
		setSearchResult(undefined);
		setCurrentPage(-1);
		setLastBatchSize(0);
		setIsError(false);
		setIsFetchingMore(false);
		setIsFetchingInitial(true);

		void fetchPage(0, 'replace');
	}, [fetchPage, filtersKey]);

	const hasMore =
		typeof resultsCount === 'number' ? results.length < resultsCount : lastBatchSize === SEARCH_LOADING_SIZE;

	useEffect(() => {
		if (currentPage < 0 || !hasMore) return;

		prefetchPage({
			...baseFilters,
			id: undefined,
			ai_search: undefined,
			page: currentPage + 1,
			size: SEARCH_LOADING_SIZE
		});
	}, [baseFilters, currentPage, hasMore, prefetchPage]);

	const loadMore = useCallback(async () => {
		if (isFetchingInitial || isFetchingMore || !hasMore) return false;

		return fetchPage(currentPage + 1, 'append');
	}, [currentPage, fetchPage, hasMore, isFetchingInitial, isFetchingMore]);

	return {
		results,
		resultsCount,
		searchResult,
		totalLoaded: results.length,
		isFetchingInitial,
		isFetchingMore,
		isError,
		hasMore,
		loadMore
	};
};
